import { useMemo, useState } from "react"
import { Modal } from "components/ui/modals/Modal/Modal"
import { Select } from "components/ui/select/Select"
import { Skeleton } from "components/ui/skeleton/Skeleton"
import { ButtonPrimary } from "components/ui/buttons/ButtonPrimary/ButtonPrimary"
import { ButtonSecondary } from "components/ui/buttons/ButtonSecondary/ButtonSecondary"
import { useUsers } from "hooks/api/user/user.hook"
import { useChangeDrawingOwner } from "hooks/api/drawings/drawings.hook"
import { useChangeOwnerModalStore } from "store/modal.store"
import { buildOwnerOptions } from "helpers/drawingOwnerChange.helper"
import { CHANGE_OWNER_MESSAGES } from "helpers/drawingOwnerChange.constants"
import type { TUser } from "types/user.type"
import style from "./ChangeOwnerModal.module.scss"

const { description, fileName, field, label, select, errorText, buttons } = style

// REQ-473: pick the new owner of a drawing. Confirming closes the modal and hands the change to
// useChangeDrawingOwner, which reports progress and the outcome in a toast.
export const ChangeOwnerModal = () => {
	const { changeOwnerFile, openCount, closeChangeOwnerModal } = useChangeOwnerModalStore()
	const isOpen = changeOwnerFile !== null

	return (
		<Modal title={CHANGE_OWNER_MESSAGES.modalTitle} isModalOpen={isOpen} setModalClose={closeChangeOwnerModal}>
			{/* antd keeps a closed dialog's children mounted, so the key — not unmounting — resets the pick */}
			{changeOwnerFile ? (
				<ChangeOwnerForm
					key={openCount}
					drawingId={changeOwnerFile.id}
					drawingName={changeOwnerFile.fileName}
					currentOwnerId={changeOwnerFile.ownerId}
					onClose={closeChangeOwnerModal}
				/>
			) : null}
		</Modal>
	)
}

type FormProps = {
	drawingId: number
	drawingName: string
	currentOwnerId: number | null
	onClose: () => void
}

const ChangeOwnerForm = ({ drawingId, drawingName, currentOwnerId, onClose }: FormProps) => {
	const [newOwnerId, setNewOwnerId] = useState<number | null>(null)
	const { data: users, isPending, isError } = useUsers()
	const { changeDrawingOwner } = useChangeDrawingOwner()

	const options = useMemo(() => buildOwnerOptions(users as TUser[] | undefined, currentOwnerId), [users, currentOwnerId])
	const newOwner = options.find((option) => option.value === newOwnerId)

	const onConfirm = () => {
		if (!newOwner) return
		onClose()
		void changeDrawingOwner({ drawingId, newOwnerId: newOwner.value, newOwnerName: newOwner.label })
	}

	return (
		<div data-testid="change-owner-modal">
			<p className={description}>
				Choose the new owner of <span className={fileName}>{drawingName}</span>. They will own the drawing with all its
				processings, quotations and files.
			</p>
			<div className={field}>
				<span className={label}>New owner</span>
				{isPending ? <Skeleton rows={1} /> : null}
				{isError ? <span className={errorText}>{CHANGE_OWNER_MESSAGES.usersFailed}</span> : null}
				{!isPending && !isError ? (
					<Select
						variant="outlined"
						className={select}
						options={options}
						value={newOwnerId}
						placeholder={CHANGE_OWNER_MESSAGES.selectPlaceholder}
						onChange={(value) => setNewOwnerId(Number(value))}
						showSearch
						optionFilterProp="label"
						placement="bottomLeft"
						notFoundContent={CHANGE_OWNER_MESSAGES.noUsers}
					/>
				) : null}
			</div>
			<div className={buttons}>
				<ButtonSecondary onClick={onClose}>Cancel</ButtonSecondary>
				<ButtonPrimary onClick={onConfirm} submittable={!!newOwner}>
					{CHANGE_OWNER_MESSAGES.confirm}
				</ButtonPrimary>
			</div>
		</div>
	)
}
